import React from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Loader2, X } from 'lucide-react';

const DEFAULT_DAYS = [1, 7, 30, 60, 90, 365];

/**
 * Modal form for creating/editing Fixed Flow campaigns
 * Extracted to separate file to avoid babel-metadata-plugin issues
 */
const FixedFlowModal = ({
  open,
  onClose,
  editingFlow,
  templates,
  onSubmit,
  saving
}) => {
  const [formData, setFormData] = React.useState({
    name: '',
    description: '',
    is_active: true,
    auto_enroll_new_clients: true,
    auto_enroll_new_orders: false,
    auto_enroll_filters: { order_sources: [], exclude_shipping: [] },
    day_templates: DEFAULT_DAYS.map(day => ({ day, template_id: '' })),
    day1_send_after_hour: null
  });
  const [newDayInput, setNewDayInput] = React.useState('');

  const ORDER_SOURCE_OPTIONS = [
    { value: 'Facebook', label: 'Facebook' },
    { value: 'Shopee', label: 'Shopee' },
    { value: 'Lazada', label: 'Lazada' },
    { value: 'Tiktok', label: 'Tiktok' },
    { value: 'Gkash', label: 'Gkash' },
    { value: 'Walk-in', label: 'Walk-in' },
  ];

  // Reset form when modal opens or editingFlow changes
  React.useEffect(() => {
    if (open) {
      if (editingFlow) {
        const existingDayTemplates = editingFlow.day_templates || [];
        const existingFilters = editingFlow.auto_enroll_filters || { order_sources: [], exclude_shipping: [] };
        // Migrate old raw values to display names
        const sourceMap = { 'fb': 'Facebook', 'gkash': 'Gkash', 'shopee': 'Shopee', 'lazada': 'Lazada', 'tiktok': 'Tiktok', 'WooCommerce': 'Website' };
        if (existingFilters.order_sources) {
          existingFilters.order_sources = existingFilters.order_sources.map(s => sourceMap[s] || s);
        }
        if (existingDayTemplates.length > 0) {
          setFormData({
            name: editingFlow.name,
            description: editingFlow.description || '',
            is_active: editingFlow.is_active,
            auto_enroll_new_clients: editingFlow.auto_enroll_new_clients,
            auto_enroll_new_orders: editingFlow.auto_enroll_new_orders || false,
            auto_enroll_filters: existingFilters,
            day_templates: existingDayTemplates.map(dt => ({
              day: dt.day,
              template_id: dt.template_id || ''
            })),
            day1_send_after_hour: editingFlow.day1_send_after_hour ?? null
          });
        } else {
          setFormData({
            name: editingFlow.name,
            description: editingFlow.description || '',
            is_active: editingFlow.is_active,
            auto_enroll_new_clients: editingFlow.auto_enroll_new_clients,
            auto_enroll_new_orders: editingFlow.auto_enroll_new_orders || false,
            auto_enroll_filters: existingFilters,
            day_templates: DEFAULT_DAYS.map(day => ({ day, template_id: '' })),
            day1_send_after_hour: editingFlow.day1_send_after_hour ?? null
          });
        }
      } else {
        setFormData({
          name: '',
          description: '',
          is_active: true,
          auto_enroll_new_clients: true,
          auto_enroll_new_orders: false,
          auto_enroll_filters: { order_sources: [], exclude_shipping: [] },
          day_templates: DEFAULT_DAYS.map(day => ({ day, template_id: '' })),
          day1_send_after_hour: null
        });
      }
      setNewDayInput('');
    }
  }, [open, editingFlow]);

  const addDayToFlow = () => {
    const dayNum = parseInt(newDayInput);
    if (isNaN(dayNum) || dayNum < 1 || dayNum > 999) {
      alert('Please enter a valid day number between 1 and 999');
      return;
    }
    
    // Check if day already exists
    let exists = false;
    for (let i = 0; i < formData.day_templates.length; i++) {
      if (formData.day_templates[i].day === dayNum) {
        exists = true;
        break;
      }
    }
    if (exists) {
      alert('Day ' + dayNum + ' already exists');
      return;
    }
    
    // Add new day
    const newArr = formData.day_templates.slice();
    newArr.push({ day: dayNum, template_id: '' });
    newArr.sort(function(a, b) { return a.day - b.day; });
    
    setFormData({ ...formData, day_templates: newArr });
    setNewDayInput('');
  };

  const removeDayFromFlow = (dayToRemove) => {
    if (formData.day_templates.length <= 1) {
      alert('You need at least one day in the flow');
      return;
    }
    const filtered = [];
    for (let i = 0; i < formData.day_templates.length; i++) {
      if (formData.day_templates[i].day !== dayToRemove) {
        filtered.push(formData.day_templates[i]);
      }
    }
    setFormData({ ...formData, day_templates: filtered });
  };

  const updateDayTemplate = (day, templateId) => {
    const actualValue = templateId === 'none' ? '' : templateId;
    const updated = formData.day_templates.map(dt =>
      dt.day === day ? { ...dt, template_id: actualValue } : dt
    );
    setFormData({ ...formData, day_templates: updated });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate at least one template is selected
    const hasTemplates = formData.day_templates.some(dt => dt.template_id);
    if (!hasTemplates) {
      alert('Please select at least one template for the flow');
      return;
    }
    
    const payload = {
      ...formData,
      day_templates: formData.day_templates.filter(dt => dt.template_id)
    };
    
    onSubmit(payload, editingFlow);
  };

  // Render template options
  const renderTemplateOptions = () => {
    const options = [<SelectItem key="none" value="none">None</SelectItem>];
    for (let i = 0; i < templates.length; i++) {
      const t = templates[i];
      options.push(<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>);
    }
    return options;
  };

  // Render day template rows
  const renderDayRows = () => {
    const rows = [];
    for (let i = 0; i < formData.day_templates.length; i++) {
      const dayTemplate = formData.day_templates[i];
      rows.push(
        <div key={dayTemplate.day} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
          <span className="w-20 font-medium text-gray-700 flex-shrink-0">Day {dayTemplate.day}</span>
          <Select
            value={dayTemplate.template_id || 'none'}
            onValueChange={(value) => updateDayTemplate(dayTemplate.day, value)}
          >
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Select template (optional)" />
            </SelectTrigger>
            <SelectContent>
              {renderTemplateOptions()}
            </SelectContent>
          </Select>
          <button
            type="button"
            onClick={() => removeDayFromFlow(dayTemplate.day)}
            className="p-2 hover:bg-red-100 rounded-lg transition-colors text-red-500"
            title="Remove this day"
          >
            <X size={16} />
          </button>
        </div>
      );
    }
    return rows;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingFlow ? 'Edit Fixed Flow' : 'Create Fixed Flow'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Flow Name *
            </label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., New Customer Welcome Flow"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the purpose of this flow..."
              rows={2}
            />
          </div>
          
          <div className="flex flex-col gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm text-gray-700">Active</span>
            </label>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.auto_enroll_new_clients}
                onChange={(e) => setFormData({ ...formData, auto_enroll_new_clients: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm text-gray-700">Auto-enroll new clients</span>
            </label>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.auto_enroll_new_orders}
                onChange={(e) => setFormData({ ...formData, auto_enroll_new_orders: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm text-gray-700">Auto-enroll on new orders</span>
              <span className="text-xs text-gray-500">(Restart from Day 1 when client places new order)</span>
            </label>

            {/* Auto-enroll filter options - shown when auto-enroll orders is ON */}
            {formData.auto_enroll_new_orders && (
              <div className="ml-6 mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-3" data-testid="auto-enroll-filters">
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Enrollment Filters</p>
                
                <div>
                  <p className="text-sm text-gray-700 mb-1.5">Order Sources (tick to include, empty = all):</p>
                  <div className="flex flex-wrap gap-3">
                    {ORDER_SOURCE_OPTIONS.map(({ value, label }) => (
                      <label key={value} className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(formData.auto_enroll_filters?.order_sources || []).includes(value)}
                          onChange={(e) => {
                            const current = formData.auto_enroll_filters?.order_sources || [];
                            const updated = e.target.checked
                              ? [...current, value]
                              : current.filter(s => s !== value);
                            setFormData({
                              ...formData,
                              auto_enroll_filters: { ...formData.auto_enroll_filters, order_sources: updated }
                            });
                          }}
                          className="rounded"
                          data-testid={`filter-source-${value.toLowerCase()}`}
                        />
                        <span className="text-sm text-gray-700">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Message Schedule (Days)
              </label>
            </div>
            
            {/* Add New Day Input */}
            <div className="flex items-center gap-2 mb-4 p-3 bg-blue-50 rounded-lg">
              <span className="text-sm text-gray-600">Add Day:</span>
              <Input
                type="number"
                min="1"
                max="999"
                value={newDayInput}
                onChange={(e) => setNewDayInput(e.target.value)}
                placeholder="e.g., 14"
                className="w-24"
              />
              <Button
                type="button"
                size="sm"
                onClick={addDayToFlow}
                variant="outline"
                className="text-blue-600 border-blue-300 hover:bg-blue-100"
              >
                <Plus size={14} className="mr-1" />
                Add
              </Button>
              <span className="text-xs text-gray-500 ml-2">Days since enrollment</span>
            </div>
            
            {/* Day Templates List */}
            <div className="space-y-3">
              {renderDayRows()}
            </div>
            
            <p className="text-xs text-gray-500 mt-3">
              Messages will be sent on the specified days after client enrollment. Only days with assigned templates will send messages.
            </p>
          </div>
          
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onClose(false)}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={saving}
              className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
            >
              {saving ? (
                <Loader2 className="animate-spin mr-2" size={16} />
              ) : null}
              {editingFlow ? 'Save Changes' : 'Create Flow'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FixedFlowModal;
